import { api } from "../lib/api";
import type { DecodedNoteResult, RetrievedNoteMetadata } from "./vaNotesService";

export interface CachedVaNote {
  id: string;
  binaryId: string;
  title: string;
  date: string;
  text: string;
}

export interface VaNotesCacheResponse {
  patient_id: number;
  notes: CachedVaNote[];
  created_at?: string;
  updated_at?: string;
  fetched_at?: string;
  cache_hit?: boolean;
}

export function mapCachedNotesToResults(
  notes: CachedVaNote[],
  metadatas: RetrievedNoteMetadata[] = []
): DecodedNoteResult[] {
  const metadataById = new Map(metadatas.map((note) => [note.id, note]));
  return notes.map((note) => {
    const metadata = metadataById.get(note.id);
    return {
      text: note.text,
      metadata: {
        id: note.id,
        binaryId: note.binaryId,
        binaryUrl: metadata?.binaryUrl || "",
        date: note.date || metadata?.date || "",
        title: note.title || metadata?.title || "Clinical Note",
        authorDisplay: metadata?.authorDisplay,
        authorDuz: metadata?.authorDuz,
        docStatus: metadata?.docStatus,
      },
    };
  });
}

export function toRetrievableNotes(
  metadatas: RetrievedNoteMetadata[]
): Array<Pick<CachedVaNote, "id" | "binaryId" | "title" | "date">> {
  return metadatas.map((note) => ({
    id: note.id,
    binaryId: note.binaryId,
    title: note.title,
    date: note.date,
  }));
}

export async function getCachedVaNotes(patientId: number): Promise<CachedVaNote[]> {
  const data = await api.get<VaNotesCacheResponse>(`/notes/todos/${patientId}/va-notes`);
  return Array.isArray(data?.notes) ? data.notes : [];
}

export async function retrieveVaNotesOnServer(
  patientId: number,
  metadatas: RetrievedNoteMetadata[]
): Promise<DecodedNoteResult[]> {
  const data = await api.post<VaNotesCacheResponse>(`/notes/todos/${patientId}/va-notes`, {
    notes: toRetrievableNotes(metadatas),
  });
  return mapCachedNotesToResults(Array.isArray(data?.notes) ? data.notes : [], metadatas);
}
